import { type ApolloError, type FetchResult } from "@apollo/client";
import { Alert, Button, Stack } from "@mui/material";
import { useState } from "react";
import {
  OrganizationMessageStatus,
  type OrganizationMessageContentInput,
  type OrganizationMessageFieldsFragment,
  useOrganizationMessageReachQuery,
  useRescheduleOrganizationMessageMutation,
  useResolveOrganizationMessageMutation,
  useWithdrawOrganizationMessageMutation,
} from "../../../generated/graphql";
import { useConfirmDialog } from "../../api-keys-admin/components/confirm-dialog";
import { throwOnGraphqlErrors } from "../../api-keys-admin/throw-on-graphql-errors";
import {
  audienceFromMessage,
  audienceInput,
  type MessageReach,
} from "../message-content";
import useSendMessage, { REFETCH_QUERIES } from "../use-send-message";
import ResolveDialog, { type FollowUpBlocked } from "./resolve-dialog";
import ScheduleDialog from "./schedule-dialog";

type Mutation = () => Promise<Pick<FetchResult<unknown>, "errors">>;

const EDITABLE_STATUSES: ReadonlySet<OrganizationMessageStatus> = new Set([
  OrganizationMessageStatus.Draft,
  OrganizationMessageStatus.Scheduled,
  OrganizationMessageStatus.Active,
]);

/** Wraps a mutation so its GraphQL errors throw. */
const checked = (mutation: Mutation) => async () => {
  throwOnGraphqlErrors(await mutation());
};

/** A follow-up to every company needs its reach, like any send to them. */
const followUpBlockedBy = (
  allCompanies: boolean,
  reach: MessageReach | undefined,
  reachError: ApolloError | undefined,
): FollowUpBlocked | null => {
  if (!allCompanies) {
    return null;
  }
  if (reachError !== undefined) {
    return {
      severity: "warning",
      reason: `Could not count who it reaches, so the follow-up cannot be sent: ${reachError.message}`,
    };
  }
  return reach === undefined
    ? { severity: "info", reason: "Counting who it reaches..." }
    : null;
};

type MessageActionsProps = {
  readonly message: OrganizationMessageFieldsFragment;
  /** Required before a draft can be sent; `undefined` while it loads. */
  readonly reach: MessageReach | undefined;
  readonly onEdit: () => void;
};

/**
 * The actions a message's status allows. Every send confirms the audience
 * and its reach first; a message to every company needs SEND typed. Sending,
 * and a follow-up to every company, are disabled until the reach has loaded,
 * and while a request runs.
 */
const MessageActions = ({ message, reach, onEdit }: MessageActionsProps) => {
  const { confirm, confirmDialog } = useConfirmDialog();
  const send = useSendMessage();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [scheduleMode, setScheduleMode] = useState<
    "schedule" | "reschedule" | null
  >(null);
  const [resolving, setResolving] = useState(false);
  const options = {
    refetchQueries: REFETCH_QUERIES,
    awaitRefetchQueries: true,
  };
  const [rescheduleMessage] = useRescheduleOrganizationMessageMutation(options);
  const [resolveMessage] = useResolveOrganizationMessageMutation(options);
  const [withdrawMessage] = useWithdrawOrganizationMessageMutation(options);
  const { data: followUpReachData, error: followUpReachError } =
    useOrganizationMessageReachQuery({
      variables: { audience: audienceInput(audienceFromMessage(message)) },
      skip: !resolving || !message.allCompanies,
      errorPolicy: "all",
    });
  const followUpReach = followUpReachData?.organizationMessageReach;

  const { uuid, status } = message;
  const isDraft = status === OrganizationMessageStatus.Draft;

  /** Runs a mutation; throws its GraphQL errors for the caller to show. */
  const run = async (mutation: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await mutation();
    } finally {
      setBusy(false);
    }
  };

  const runShown = async (mutation: () => Promise<void>): Promise<void> => {
    try {
      await run(mutation);
    } catch (runError) {
      setError(runError instanceof Error ? runError.message : String(runError));
    }
  };

  const handleSendNow = async () => {
    const confirmed = await send.confirmSend(message, reach, null);
    if (confirmed !== undefined) {
      await runShown(async () =>
        send.deliver(uuid, confirmed.confirmation, null),
      );
    }
  };

  const handleSchedule = async (startsAt: Date) => {
    if (scheduleMode === "reschedule") {
      await run(
        checked(async () =>
          rescheduleMessage({ variables: { input: { uuid, startsAt } } }),
        ),
      );
    } else {
      const confirmed = await send.confirmSend(message, reach, startsAt);
      if (confirmed === undefined) {
        return;
      }
      await run(async () =>
        send.deliver(uuid, confirmed.confirmation, startsAt),
      );
    }
    setScheduleMode(null);
  };

  /** A follow-up to every company is a new send, so it needs SEND typed too. */
  const handleResolve = async (
    followUp: OrganizationMessageContentInput | null,
  ) => {
    let confirmation: string | null = null;
    if (followUp !== null && message.allCompanies) {
      const confirmed = await send.confirmSend(
        { ...message, severity: followUp.severity },
        followUpReach,
        null,
      );
      if (confirmed === undefined) {
        return;
      }
      confirmation = confirmed.confirmation;
    }
    await run(
      checked(async () =>
        resolveMessage({
          variables: { input: { uuid, followUp, confirmation } },
        }),
      ),
    );
    setResolving(false);
  };

  const handleWithdraw = async () => {
    try {
      await confirm({
        title: isDraft ? "Discard this draft?" : "Withdraw this message?",
        description: isDraft
          ? undefined
          : "It disappears from every bell and banner, and a scheduled delivery is cancelled.",
        confirmationText: isDraft ? "Discard" : "Withdraw",
      });
    } catch {
      return;
    }
    await runShown(
      checked(async () => withdrawMessage({ variables: { input: { uuid } } })),
    );
  };

  const canEdit = EDITABLE_STATUSES.has(status);
  const sendDisabled = busy || reach === undefined;
  const followUpBlocked = followUpBlockedBy(
    message.allCompanies,
    followUpReach,
    followUpReachError,
  );

  return (
    <Stack spacing={1}>
      {confirmDialog}
      {send.confirmDialog}
      {error !== null && <Alert severity="error">{error}</Alert>}
      <Stack useFlexGap direction="row" spacing={1} flexWrap="wrap">
        {isDraft && (
          <>
            <Button
              variant="contained"
              disabled={sendDisabled}
              onClick={() => {
                void handleSendNow();
              }}
            >
              Send now
            </Button>
            <Button
              variant="outlined"
              disabled={sendDisabled}
              onClick={() => {
                setScheduleMode("schedule");
              }}
            >
              Schedule
            </Button>
          </>
        )}
        {canEdit && (
          <Button variant="outlined" disabled={busy} onClick={onEdit}>
            Edit
          </Button>
        )}
        {status === OrganizationMessageStatus.Scheduled && (
          <Button
            variant="outlined"
            disabled={busy}
            onClick={() => {
              setScheduleMode("reschedule");
            }}
          >
            Reschedule
          </Button>
        )}
        {status === OrganizationMessageStatus.Active && (
          <Button
            variant="outlined"
            disabled={busy}
            onClick={() => {
              setResolving(true);
            }}
          >
            Resolve
          </Button>
        )}
        {canEdit && (
          <Button
            color="error"
            disabled={busy}
            onClick={() => {
              void handleWithdraw();
            }}
          >
            {isDraft
              ? "Discard"
              : status === OrganizationMessageStatus.Scheduled
                ? "Cancel"
                : "Withdraw"}
          </Button>
        )}
      </Stack>
      <ScheduleDialog
        open={scheduleMode !== null}
        title={scheduleMode === "reschedule" ? "Reschedule" : "Schedule"}
        initial={
          message.startsAt === null ? null : new Date(String(message.startsAt))
        }
        onClose={() => {
          setScheduleMode(null);
        }}
        onSubmit={handleSchedule}
      />
      <ResolveDialog
        open={resolving}
        resolvedTitle={message.title}
        followUpBlocked={followUpBlocked}
        onClose={() => {
          setResolving(false);
        }}
        onSubmit={handleResolve}
      />
    </Stack>
  );
};

export default MessageActions;
